import { createContext, useEffect, useReducer } from "react";

import api from "./../api";
import { isValidToken, setSession } from "../utils/jwt";

const INITIALIZE = "INITIALIZE";
const SIGN_IN = "SIGN_IN";
const SIGN_OUT = "SIGN_OUT";
const SIGN_UP = "SIGN_UP";
const ACCOUNT_TOKEN_EMAIL = "ACCOUNT_TOKEN_EMAIL";

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  accountTokenEmail: "", // email address retrieved from valid token of signup and password reset
};

const JWTReducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE:
      return {
        isAuthenticated: action.payload.isAuthenticated,
        isInitialized: true,
        user: action.payload.user,
        accountTokenEmail: "",
      };

    case SIGN_IN:
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        accountTokenEmail: "",
      };

    case SIGN_OUT:
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        accountTokenEmail: "",
      };

    case SIGN_UP:
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        accountTokenEmail: "",
      };

    case ACCOUNT_TOKEN_EMAIL:
      return {
        ...state,
        accountTokenEmail: action.payload.accountTokenEmail,
      };

    default:
      return state;
  }
};

const AuthContext = createContext(null);

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(JWTReducer, initialState);

  useEffect(() => {
    initialize();
  }, []);

  const initialize = async () => {
    try {
      const accessToken = window.localStorage.getItem("accessToken");

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);
        const response = await api.post("accounts/profile");

        const user = response.data;

        dispatch({
          type: INITIALIZE,
          payload: {
            isAuthenticated: true,
            user,
          },
        });
      } else {
        dispatch({
          type: INITIALIZE,
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    } catch (err) {
      console.error(err);
      dispatch({
        type: INITIALIZE,
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  };

  const getProfile = async () => {
    try {
      const response = await api.post("accounts/profile");
      const user = response.data;
      dispatch({
        type: INITIALIZE,
        payload: {
          isAuthenticated: true,
          user,
        },
      });
    } catch (error) {
      dispatch({ type: SIGN_OUT });
    }
  };

  const signIn = async (email, password) => {
    const response = await api.post("accounts/login", {
      email,
      password,
    });
    const { accessToken, user } = response.data;

    setSession(accessToken);
    dispatch({
      type: SIGN_IN,
      payload: {
        user,
      },
    });
    initialize();
  };

  const updateProfile = (user) => {
    dispatch({
      type: INITIALIZE,
      payload: {
        isAuthenticated: true,
        user,
      },
    });
  };

  const signOut = async () => {
    setSession(null);
    dispatch({ type: SIGN_OUT });
  };

  const signUp = async (email, password, firstName, lastName) => {
    const response = await api.post("/api/auth/sign-up", {
      email,
      password,
      firstName,
      lastName,
    });
    const { accessToken, user } = response.data;

    window.localStorage.setItem("accessToken", accessToken);
    dispatch({
      type: SIGN_UP,
      payload: {
        user,
      },
    });
  };

  const resetPassword = (email) => console.log(email);

  const setAccountTokenEmail = (email) => {
    dispatch({
      type: ACCOUNT_TOKEN_EMAIL,
      payload: {
        accountTokenEmail: email,
      },
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "jwt",
        signIn,
        signOut,
        signUp,
        updateProfile,
        resetPassword,
        getProfile,
        setAccountTokenEmail,
      }}
    >
      {state.isInitialized && children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
